import { Droplets, Milk, Moon, Plus, Timer, Waves } from 'lucide-react';

import { buildStats, localDateKey } from '../lib/stats.js';
import { formatDuration, POOP_LEVELS } from '../lib/format.js';
import DailyBars from './DailyBars.jsx';
import RangeSwitch from './RangeSwitch.jsx';
import Timeline from './Timeline.jsx';

function eventsInRange(events, stats, kinds) {
  return events.filter((event) => {
    const key = localDateKey(event.startedAt);
    return kinds.includes(event.kind) && key >= stats.startDate && key <= stats.endDate;
  });
}

function PageHeader({ eyebrow, title, range, onRangeChange, onAdd }) {
  return (
    <header className="detail-header">
      <div className="detail-header__title"><span className="eyebrow">{eyebrow}</span><h1>{title}</h1></div>
      <button type="button" className="icon-button icon-button--solid" onClick={onAdd} aria-label="新增记录"><Plus size={20} /></button>
      <RangeSwitch value={range} onChange={onRangeChange} />
    </header>
  );
}

export function FeedPage({ events, range, onRangeChange, onAdd, onEdit, onDelete, now }) {
  const stats = buildStats(events, range, now);
  const timeline = eventsInRange(events, stats, ['feed']);
  const bottleTotal = stats.feed.formulaMl + stats.feed.breastmilkMl;
  return (
    <div className="page page--detail">
      <PageHeader eyebrow="喂养统计" title="吃奶" range={range} onRangeChange={onRangeChange} onAdd={() => onAdd('feed')} />
      <section className="metric-grid metric-grid--three">
        <div className="metric"><Milk size={19} /><span>瓶喂总量</span><strong>{bottleTotal}<small> ml</small></strong><em>母乳与奶粉合计</em></div>
        <div className="metric"><Timer size={19} /><span>亲喂时长</span><strong>{stats.feed.nursingMinutes}<small> 分</small></strong><em>不折算为毫升</em></div>
        <div className="metric"><Waves size={19} /><span>喂养次数</span><strong>{stats.feed.sessions}<small> 次</small></strong><em>本周期已记录</em></div>
      </section>
      <section className="data-section">
        <div className="section-heading"><div><span className="eyebrow">毫升</span><h2>瓶喂趋势</h2></div><span className="section-meta">不含亲喂</span></div>
        <DailyBars daily={stats.daily} empty={!stats.feed.formulaMl && !stats.feed.breastmilkMl} series={[
          { label: '母乳瓶喂', unit: 'ml', color: '#e76855', value: (day) => day.breastmilkMl },
          { label: '奶粉', unit: 'ml', color: '#d4a62e', value: (day) => day.formulaMl },
        ]} />
      </section>
      <section className="data-section">
        <div className="section-heading"><div><span className="eyebrow">分钟</span><h2>亲喂趋势</h2></div><span className="section-meta">左 / 右 / 双侧</span></div>
        <DailyBars daily={stats.daily} empty={!stats.feed.nursingMinutes} series={[
          { label: '亲喂分钟', unit: '分钟', color: '#3d8c7d', value: (day) => day.nursingMinutes },
        ]} />
      </section>
      <section className="content-section">
        <div className="section-heading"><div><span className="eyebrow">原始记录</span><h2>喂养时间线</h2></div><span className="section-meta">{timeline.length} 条</span></div>
        <Timeline events={timeline} onEdit={onEdit} onDelete={onDelete} emptyText="当前范围内没有喂养记录" />
      </section>
    </div>
  );
}

export function DiaperPage({ events, range, onRangeChange, onAdd, onEdit, onDelete, now }) {
  const stats = buildStats(events, range, now);
  const timeline = eventsInRange(events, stats, ['poop', 'pee']);
  const maxLevel = Math.max(1, ...stats.diaper.poopLevels);
  return (
    <div className="page page--detail">
      <PageHeader eyebrow="排泄统计" title="拉与尿" range={range} onRangeChange={onRangeChange} onAdd={() => onAdd('poop')} />
      <section className="metric-grid metric-grid--two">
        <div className="metric metric--poop"><Waves size={19} /><span>臭臭</span><strong>{stats.diaper.poopCount}<small> 次</small></strong><em>按五档便量记录</em></div>
        <div className="metric metric--pee"><Droplets size={19} /><span>小便</span><strong>{stats.diaper.peeCount}<small> 次</small></strong><em>按三档尿量记录</em></div>
      </section>
      <section className="data-section">
        <div className="section-heading"><div><span className="eyebrow">次数</span><h2>每日变化</h2></div><span className="section-meta">记录行为，不代表医学目标</span></div>
        <DailyBars daily={stats.daily} empty={!stats.diaper.poopCount && !stats.diaper.peeCount} series={[
          { label: '臭臭', unit: '次', color: '#c58e27', value: (day) => day.poopCount },
          { label: '小便', unit: '次', color: '#389a9a', value: (day) => day.peeCount },
        ]} />
      </section>
      <section className="data-section">
        <div className="section-heading"><div><span className="eyebrow">便量分布</span><h2>五档记录</h2></div></div>
        {stats.diaper.poopCount ? (
          <div className="level-distribution">
            {POOP_LEVELS.map((label, index) => (
              <div key={label}><span>{label}</span><i><b style={{ width: `${(stats.diaper.poopLevels[index] / maxLevel) * 100}%` }} /></i><strong>{stats.diaper.poopLevels[index]}</strong></div>
            ))}
          </div>
        ) : <div className="chart-empty"><span>暂无臭臭记录</span></div>}
      </section>
      <section className="content-section">
        <div className="section-heading"><div><span className="eyebrow">原始记录</span><h2>便尿时间线</h2></div><span className="section-meta">{timeline.length} 条</span></div>
        <Timeline events={timeline} onEdit={onEdit} onDelete={onDelete} emptyText="当前范围内没有便尿记录" />
      </section>
    </div>
  );
}

export function SleepPage({ events, activeSleep, range, onRangeChange, onAdd, onEdit, onDelete, onToggleSleep, now }) {
  const stats = buildStats(events, range, now);
  const timeline = eventsInRange(events, stats, ['sleep']);
  const activeMinutes = activeSleep ? Math.floor((now - new Date(activeSleep.startedAt)) / 60000) : 0;
  return (
    <div className="page page--detail">
      <PageHeader eyebrow="睡眠统计" title="睡觉" range={range} onRangeChange={onRangeChange} onAdd={() => onAdd('sleep')} />
      {activeSleep ? (
        <button type="button" className="sleep-control sleep-control--active" onClick={onToggleSleep}>
          <span className="active-sleep-banner__pulse" /><span><strong>睡眠计时中</strong><small>{formatDuration(activeMinutes)}</small></span><b>轻点结束</b>
        </button>
      ) : (
        <button type="button" className="sleep-control" onClick={onToggleSleep}><Moon size={24} /><span><strong>宝宝醒着</strong><small>轻点开始睡眠计时</small></span><b>开始</b></button>
      )}
      <section className="metric-grid metric-grid--two">
        <div className="metric"><Moon size={19} /><span>完整睡眠</span><strong>{formatDuration(stats.sleep.minutes)}</strong><em>不含进行中的睡眠</em></div>
        <div className="metric"><Timer size={19} /><span>睡眠次数</span><strong>{stats.sleep.sessions}<small> 次</small></strong><em>{stats.sleep.activeSessions ? '另有 1 次进行中' : '本周期已结束'}</em></div>
      </section>
      <section className="data-section">
        <div className="section-heading"><div><span className="eyebrow">分钟</span><h2>每日睡眠</h2></div><span className="section-meta">仅完整会话</span></div>
        <DailyBars daily={stats.daily} empty={!stats.sleep.minutes} series={[
          { label: '睡眠分钟', unit: '分钟', color: '#596baa', value: (day) => day.sleepMinutes },
        ]} />
      </section>
      <section className="content-section">
        <div className="section-heading"><div><span className="eyebrow">原始记录</span><h2>睡眠时间线</h2></div><span className="section-meta">{timeline.length} 条</span></div>
        <Timeline events={timeline} onEdit={onEdit} onDelete={onDelete} emptyText="当前范围内没有睡眠记录" />
      </section>
    </div>
  );
}
